/// <amd-module name="Repos/RepoDropdown/RepositoryDropdownProvider" />
import { Favorite } from "Favorites/Common/Contracts/Favorite";
import { IFavoritesCollection } from "Favorites/Common/FavoritesService";
import { ArtifactDropdownProviderBase, IArtifactDropdownProviderOptions, IFavoritesContext } from "Favorites/Dropdown/ArtifactDropdownProvider";
import { GitRepository } from "Repos/Common/Contracts/Git";
import { IReadyableReadonlyObservableArray, ReadyableObservableArray } from "VSS/Core/Observable";
import { localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { IIconProps } from "VSSUI/Icon";
import { IGitRepositoryService } from "./GitRepositoryService";

/**
 * An item in a repository picker
 */
export interface IRepositoryDropdownItem {
    /**
     * Display name of the repository ($/ProjectName for TFVC)
     */
    name: string;

    /**
     * If true, this represents the single TFVC repository
     */
    isTfvc?: boolean;

    /**
     * If true, this git repository is a fork
     */
    isFork?: boolean;

    /**
     * Repository id
     */
    id: string;

    /**
     * Git repository information (if a git repository)
     */
    repository?: GitRepository;
}

export interface IRepositoryDropdownOptions {
    /**
     * Optional delegate to get the URL for the given item.
     */
    getArtifactHref?: (item: IRepositoryDropdownItem) => string;

    /**
     * The name of the project under which to show repositories
     */
    projectName?: string;

    /**
     * The id of the project under which to show repositories
     */
    projectId?: string;

    /**
     * Optional callback to be notified when favorites have finished loading.
     */
    onFavoritesLoaded?: () => void;

    /**
     * Optionally supply the set of repositories to use in the dropdown.
     * If not supplied, all repositories in the project are fetched and shown.
     */
    repositories?: GitRepository[];

    /**
     * Does the current project support TFVC?
     */
    supportsTfvc?: boolean;

    /**
     * Header text for the default group
     */
    defaultGroupHeader?: string;

    /**
     * Header text for the favorites group
     */
    favoriteGroupHeader?: string;

    /**
     * Show favorites as soon as they are available, instead of waiting for repositories to be loaded. True by default.
     * This may cause that favorites displayed are not present on the items, so if your repositories list is filtered out,
     * you may want to disable this option to prevent leaks.
     */
    showFavoritesEarly?: boolean;

    /**
     * Optional callback to be notified repositories have finished loading.
     */
    onRepositoriesLoaded?: (repositories: GitRepository[]) => void;
}

const RepositoryFavoriteArtifactType = "Microsoft.TeamFoundation.Git.Repository";

export class RepositoryDropdownProvider extends ArtifactDropdownProviderBase<IRepositoryDropdownItem> {
    private items: ReadyableObservableArray<IRepositoryDropdownItem>;
    private pageContext: IVssPageContext;
    private projectId: string | undefined;
    private projectName: string | undefined;
    private repositories: GitRepository[] | undefined;
    private supportsTfvc: boolean | undefined;
    private artifactPickerOptions: IArtifactDropdownProviderOptions<IRepositoryDropdownItem>;
    private onRepositoriesLoaded?: (repositories: GitRepository[]) => void;

    constructor(pageContext: IVssPageContext, options: IRepositoryDropdownOptions) {
        super();

        const {
            getArtifactHref,
            onFavoritesLoaded,
            onRepositoriesLoaded,
            supportsTfvc,
            projectName,
            projectId,
            repositories,
            defaultGroupHeader,
            favoriteGroupHeader,
            showFavoritesEarly = true
        } = options;

        this.pageContext = pageContext;
        this.projectId = projectId;
        this.projectName = projectName;
        this.repositories = repositories;
        this.supportsTfvc = supportsTfvc;
        this.onRepositoriesLoaded = onRepositoriesLoaded;

        let favoritesContext: IFavoritesContext;
        if (projectName && projectId) {
            favoritesContext = {
                artifactType: RepositoryFavoriteArtifactType,
                artifactScope: {
                    id: projectId,
                    name: projectName,
                    type: "Project"
                }
            };
        } else {
            favoritesContext = {
                artifactType: RepositoryFavoriteArtifactType,
                artifactScope: {
                    id: "",
                    name: "",
                    type: "Collection"
                }
            };
        }

        this.artifactPickerOptions = {
            pageContext,
            favoritesContext,
            onFavoritesLoaded,
            defaultGroupHeader,
            favoriteGroupHeader,
            getArtifacts: this.getDropdownItems,
            getArtifactHref,
            getArtifactId: repo => repo.id,
            getArtifactName: repo => repo.name,
            getArtifactIcon: (repo: IRepositoryDropdownItem): IIconProps => {
                return {
                    iconName: repo.isTfvc ? "TFVCLogo" : repo.isFork ? "GitFork" : "GitLogo",
                    className: repo.isTfvc ? "repos-tfvc-icon" : "repos-git-icon"
                };
            },
            getArtifactFromFavorite: showFavoritesEarly
                ? favorite => {
                    return {
                        name: favorite.artifactName,
                        id: favorite.artifactId,
                        isTfvc: false,
                        isFork: false
                    };
                }
                : undefined,
            getFavoriteFromArtifact: repo => {
                if (repo.repository) {
                    return {
                        artifactId: repo.repository.id,
                        artifactName: repo.repository.name,
                        artifactScope: {
                            id: repo.repository.project.id,
                            name: repo.repository.project.name,
                            type: "Project"
                        },
                        artifactType: RepositoryFavoriteArtifactType
                    } as Favorite;
                } else {
                    return undefined;
                }
            }
        };

        this.initialize(this.artifactPickerOptions);
    }
    
    protected async loadFavorites(): Promise<IFavoritesCollection | undefined> {
        const performanceService = this.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        performanceService.startScenario("repo-dropdown-load-favorites", false);
        const favorites = await super.loadFavorites();

        if (favorites) {
            performanceService.endScenario("Repos", "repo-dropdown-load-favorites", false);
        }

        return favorites;
    }

    private getDropdownItems = (): IReadyableReadonlyObservableArray<IRepositoryDropdownItem> => {
        if (!this.items) {
            const performanceService = this.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
            performanceService.startScenario("repo-dropdown-get-all-repositories", false);

            this.items = new ReadyableObservableArray<IRepositoryDropdownItem>();

            let repositories: IReadyableReadonlyObservableArray<GitRepository>;
            if (this.repositories) {
                repositories = new ReadyableObservableArray(this.repositories, true);
            } else {
                const repositoryService = this.pageContext.getService<IGitRepositoryService>("IGitRepositoryService");
                repositories = repositoryService.getAllRepositories(this.projectId);
            }

            const process = (repositories: GitRepository[]) => {
                const repoListItems = repositories.map(createRepositoryDropdownItem);

                // Sort the git repos alphabetically by name
                repoListItems.sort((repo1, repo2) => {
                    return localeIgnoreCaseComparer(repo1.name, repo2.name);
                });

                // Add a TFVC entry to the front of the list if the project supports it
                if (this.supportsTfvc) {
                    repoListItems.unshift({
                        name: "$/" + this.projectName,
                        isTfvc: true,
                        id: "tfvc",
                        isFork: false
                    });
                }

                performanceService.endScenario("Repos", "repo-dropdown-get-all-repositories", false);

                this.onRepositoriesLoaded?.(repositories);
                this.items.value = repoListItems;
                this.items.ready.value = true;
            };

            if (repositories.ready.value) {
                process(repositories.value);
            } else {
                const observer = (value: boolean) => {
                    if (value) {
                        process(repositories.value);
                    }
                };
                this.addObserver(repositories.ready, observer);
            }
        }
        return this.items;
    };
}

function createRepositoryDropdownItem(repo: GitRepository): IRepositoryDropdownItem {
    return {
        name: repo.name,
        id: repo.id,
        repository: repo,
        isFork: repo.isFork,
        isTfvc: false
    };
}
